import { useState, useEffect, useRef } from 'react';
import { listVerzekeringen, createVerzekering, updateVerzekering, deleteVerzekering, uploadVerzekeringPhoto, getLookup } from '../api';

const COLOR_PALETTE = [
  { bg: '#e0f2fe', color: '#0369a1' },
  { bg: '#dbeafe', color: '#1d4ed8' },
  { bg: '#d1fae5', color: '#065f46' },
  { bg: '#fef3c7', color: '#b45309' },
  { bg: '#ede9fe', color: '#6d28d9' },
  { bg: '#fee2e2', color: '#b91c1c' },
  { bg: '#dcfce7', color: '#166534' },
  { bg: '#f3e8ff', color: '#7c3aed' },
];

function typeColor(value, allTypes) {
  const idx = allTypes.indexOf(value);
  return COLOR_PALETTE[(idx >= 0 ? idx : 0) % COLOR_PALETTE.length];
}

const photoUrl = (filename) => `/uploads/verzekeringen/${filename}`;

const EMPTY_FORM = { naam: '', verzekeraar: '', polisnummer: '', type: '', geldig_van: '', geldig_tot: '', notities: '' };

function fmtDate(d) {
  if (!d) return null;
  return String(d).slice(0, 10);
}

export default function VerzekeringPage() {
  const [verzekeringen, setVerzekeringen] = useState([]);
  const [types,         setTypes]         = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [showForm,      setShowForm]      = useState(false);
  const [editId,        setEditId]        = useState(null);
  const [form,          setForm]          = useState(EMPTY_FORM);
  const [saving,        setSaving]        = useState(false);
  const [zoom,          setZoom]          = useState(null);
  const frontRef = useRef();
  const backRef  = useRef();

  const load = () => {
    setLoading(true);
    Promise.all([
      listVerzekeringen(),
      getLookup('verzekering_type'),
    ]).then(([v, t]) => {
      setVerzekeringen(v);
      setTypes(t.map(item => item.value));
    }).catch(() => {
      setVerzekeringen([]);
      setTypes(['DAN', 'Duikverzekering', 'BA', 'Reisverzekering']);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!zoom) return;
    const h = e => { if (e.key === 'Escape') setZoom(null); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [zoom]);

  const openAdd = () => {
    setEditId(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (v) => {
    setEditId(v.id);
    setForm({
      naam:        v.naam        || '',
      verzekeraar: v.verzekeraar || '',
      polisnummer: v.polisnummer || '',
      type:        v.type        || '',
      geldig_van:  fmtDate(v.geldig_van) || '',
      geldig_tot:  fmtDate(v.geldig_tot) || '',
      notities:    v.notities    || '',
    });
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.naam.trim()) return;
    setSaving(true);
    try {
      if (editId) {
        const updated = await updateVerzekering(editId, form);
        setVerzekeringen(prev => prev.map(v => v.id === editId ? updated : v));
      } else {
        const fd = new FormData();
        Object.entries(form).forEach(([k, val]) => fd.append(k, val));
        if (frontRef.current?.files[0]) fd.append('photo_front', frontRef.current.files[0]);
        if (backRef.current?.files[0])  fd.append('photo_back',  backRef.current.files[0]);
        const created = await createVerzekering(fd);
        setVerzekeringen(prev => [created, ...prev]);
      }
      setShowForm(false);
    } catch (err) {
      alert('Opslaan mislukt: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Verzekering verwijderen?')) return;
    await deleteVerzekering(id);
    setVerzekeringen(prev => prev.filter(v => v.id !== id));
  };

  const handlePhotoUpload = async (verzId, side, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('photo', file);
    try {
      const { filename } = await uploadVerzekeringPhoto(verzId, side, fd);
      setVerzekeringen(prev => prev.map(v =>
        v.id === verzId ? { ...v, [`photo_${side}`]: filename } : v
      ));
    } catch (err) {
      alert('Foto uploaden mislukt: ' + err.message);
    }
  };

  const setF = (k, val) => setForm(f => ({ ...f, [k]: val }));

  const inputStyle = {
    padding: '8px 10px', borderRadius: 8,
    border: '1.5px solid var(--border)',
    fontSize: 14, background: 'var(--card)',
    color: 'var(--text)', width: '100%', boxSizing: 'border-box',
  };

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: '0 0 40px' }}>

      {/* Header */}
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 className="page-title">🛡️ Verzekeringen</h1>
          <p className="page-subtitle">Duik- en reisverzekeringen</p>
        </div>
        <button className="btn btn-primary" onClick={openAdd} style={{ fontSize: 13 }}>
          ➕ Verzekering toevoegen
        </button>
      </div>

      {/* Lege staat */}
      {!loading && verzekeringen.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: 48, color: 'var(--text-muted)' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🛡️</div>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Nog geen verzekeringen toegevoegd</div>
          <div style={{ fontSize: 13 }}>Voeg je polis- en verzekeringskaartjes toe via de knop bovenaan</div>
        </div>
      )}

      {/* Lijst */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {verzekeringen.map(v => {
          const tc = typeColor(v.type, types);
          const isExpired   = v.geldig_tot && new Date(v.geldig_tot) < new Date();
          const expiresSoon = v.geldig_tot && !isExpired &&
            (new Date(v.geldig_tot) - new Date()) < 90 * 24 * 3600 * 1000;

          return (
            <div key={v.id} className="card" style={{ padding: 20 }}>

              {/* Bovenste rij */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
                {v.type && (
                  <span style={{
                    background: tc.bg, color: tc.color,
                    fontWeight: 800, fontSize: 14, borderRadius: 10,
                    padding: '6px 14px', flexShrink: 0, letterSpacing: 0.5,
                  }}>
                    {v.type}
                  </span>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 16 }}>{v.naam}</div>
                  {v.verzekeraar && (
                    <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>
                      {v.verzekeraar}
                      {v.polisnummer && <span style={{ marginLeft: 10, fontFamily: 'monospace', fontSize: 12, background: 'var(--surface)', padding: '1px 6px', borderRadius: 4 }}>{v.polisnummer}</span>}
                    </div>
                  )}
                  {(v.geldig_van || v.geldig_tot) && (
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>
                      {fmtDate(v.geldig_van) || '?'} → {fmtDate(v.geldig_tot) || '?'}
                      {isExpired   && <span style={{ marginLeft: 8, color: '#dc2626', fontWeight: 600 }}>● Vervallen</span>}
                      {expiresSoon && <span style={{ marginLeft: 8, color: '#d97706', fontWeight: 600 }}>● Verloopt binnenkort</span>}
                    </div>
                  )}
                  {v.notities && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{v.notities}</div>}
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button onClick={() => openEdit(v)}
                    style={{ padding: '5px 12px', borderRadius: 7, border: '1px solid var(--border)', background: 'none', color: 'var(--text-muted)', fontSize: 13, cursor: 'pointer' }}>
                    ✏️
                  </button>
                  <button onClick={() => handleDelete(v.id)}
                    style={{ padding: '5px 10px', borderRadius: 7, border: 'none', background: 'rgba(239,68,68,.1)', color: '#ef4444', fontSize: 13, cursor: 'pointer' }}>
                    🗑️
                  </button>
                </div>
              </div>

              {/* Foto's */}
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {['front', 'back'].map(side => {
                  const filename = v[`photo_${side}`];
                  const label    = side === 'front' ? 'Voorkant' : 'Achterkant';
                  const inputId  = `photo-verz-${v.id}-${side}`;
                  return (
                    <div key={side} style={{ flex: '1 1 200px', minWidth: 160, maxWidth: 280 }}>
                      {filename ? (
                        <div style={{ position: 'relative' }}>
                          <img
                            src={photoUrl(filename)}
                            alt={label}
                            onClick={() => setZoom({ url: photoUrl(filename), label: `${v.naam} — ${label}` })}
                            style={{ width: '100%', borderRadius: 10, objectFit: 'cover', maxHeight: 160, display: 'block', cursor: 'zoom-in' }}
                          />
                          <label htmlFor={inputId}
                            style={{
                              position: 'absolute', bottom: 7, right: 7,
                              background: 'rgba(0,0,0,.55)', color: '#fff',
                              borderRadius: 6, padding: '3px 9px', fontSize: 11,
                              cursor: 'pointer', fontWeight: 600,
                            }}>
                            🔄 Vervangen
                          </label>
                          <input id={inputId} type="file" accept="image/*" capture="environment"
                            style={{ display: 'none' }}
                            onChange={e => handlePhotoUpload(v.id, side, e.target.files[0])} />
                        </div>
                      ) : (
                        <label htmlFor={inputId}
                          style={{
                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                            gap: 6, height: 120, borderRadius: 10, cursor: 'pointer',
                            border: '2px dashed var(--border)', background: 'var(--card)',
                            color: 'var(--text-muted)', fontSize: 12, fontWeight: 500,
                          }}>
                          <span style={{ fontSize: 28 }}>📷</span>
                          {label} toevoegen
                          <input id={inputId} type="file" accept="image/*" capture="environment"
                            style={{ display: 'none' }}
                            onChange={e => handlePhotoUpload(v.id, side, e.target.files[0])} />
                        </label>
                      )}
                    </div>
                  );
                })}
              </div>

            </div>
          );
        })}
      </div>

      {/* Formulier modal */}
      {showForm && (
        <div onClick={() => setShowForm(false)} style={{
          position: 'fixed', inset: 0, zIndex: 9000,
          background: 'rgba(0,0,0,.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: 'var(--surface)', borderRadius: 14,
            width: '100%', maxWidth: 480,
            boxShadow: '0 20px 60px rgba(0,0,0,.4)',
            maxHeight: '90vh', overflowY: 'auto',
          }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>
                {editId ? '✏️ Verzekering bewerken' : '🛡️ Verzekering toevoegen'}
              </h2>
              <button onClick={() => setShowForm(false)}
                style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text-muted)' }}>✕</button>
            </div>

            <form onSubmit={handleSave} style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>

              {/* Naam */}
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>
                NAAM *
                <input type="text" required placeholder="bv. DAN Europe 2025" value={form.naam}
                  onChange={e => setF('naam', e.target.value)} style={inputStyle} />
              </label>

              {/* Type */}
              {types.length > 0 && (
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>
                  TYPE
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {types.map(t => {
                      const tc = typeColor(t, types);
                      const selected = form.type === t;
                      return (
                        <button key={t} type="button"
                          onClick={() => setF('type', selected ? '' : t)}
                          style={{
                            padding: '6px 14px', borderRadius: 8, fontWeight: 700, fontSize: 13,
                            cursor: 'pointer', border: selected ? '2px solid currentColor' : '2px solid transparent',
                            background: selected ? tc.bg : 'var(--card)',
                            color: selected ? tc.color : 'var(--text-muted)',
                          }}>
                          {t}
                        </button>
                      );
                    })}
                  </div>
                </label>
              )}

              {/* Verzekeraar + Polisnummer */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>
                  VERZEKERAAR
                  <input type="text" placeholder="bv. DAN Europe" value={form.verzekeraar}
                    onChange={e => setF('verzekeraar', e.target.value)} style={inputStyle} />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>
                  POLISNUMMER
                  <input type="text" placeholder="bv. BE-123456" value={form.polisnummer}
                    onChange={e => setF('polisnummer', e.target.value)} style={inputStyle} />
                </label>
              </div>

              {/* Geldigheid */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>
                  GELDIG VAN
                  <input type="date" value={form.geldig_van} onChange={e => setF('geldig_van', e.target.value)} style={inputStyle} />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>
                  GELDIG TOT
                  <input type="date" value={form.geldig_tot} onChange={e => setF('geldig_tot', e.target.value)} style={inputStyle} />
                </label>
              </div>

              {/* Notities */}
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>
                NOTITIES
                <input type="text" placeholder="bv. Dekkingsgebied, contactnummer…" value={form.notities}
                  onChange={e => setF('notities', e.target.value)} style={inputStyle} />
              </label>

              {/* Foto's — enkel bij nieuw */}
              {!editId && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  {[{ ref: frontRef, label: 'VOORKANT' }, { ref: backRef, label: 'ACHTERKANT' }].map(({ ref, label }) => (
                    <label key={label} style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>
                      {label} (optioneel)
                      <input type="file" accept="image/*" capture="environment" ref={ref}
                        style={{ fontSize: 12, color: 'var(--text-muted)' }} />
                    </label>
                  ))}
                </div>
              )}

              {/* Knoppen */}
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', paddingTop: 4 }}>
                <button type="button" onClick={() => setShowForm(false)}
                  style={{ padding: '9px 18px', borderRadius: 8, border: '1.5px solid var(--border)', background: 'none', color: 'var(--text-muted)', fontSize: 14, cursor: 'pointer' }}>
                  Annuleren
                </button>
                <button type="submit" disabled={!form.naam.trim() || saving}
                  style={{
                    padding: '9px 22px', borderRadius: 8, border: 'none',
                    fontSize: 14, fontWeight: 600, cursor: 'pointer',
                    background: form.naam.trim() && !saving ? 'var(--blue)' : 'var(--border)',
                    color: form.naam.trim() && !saving ? '#fff' : 'var(--text-muted)',
                  }}>
                  {saving ? '⏳ Opslaan…' : '✅ Opslaan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Zoom overlay */}
      {zoom && (
        <div onClick={() => setZoom(null)} style={{
          position: 'fixed', inset: 0, zIndex: 10001,
          background: 'rgba(0,0,0,.95)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          padding: 16, cursor: 'zoom-out',
        }}>
          <div style={{ marginBottom: 10, color: '#fff', fontWeight: 600, fontSize: 14 }}>{zoom.label}</div>
          <img src={zoom.url} alt={zoom.label}
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: '100%', maxHeight: 'calc(100vh - 100px)', borderRadius: 10, objectFit: 'contain' }} />
          <div style={{ marginTop: 10, fontSize: 12, color: 'rgba(255,255,255,.4)' }}>Klik of druk Esc om te sluiten</div>
          <button onClick={() => setZoom(null)} style={{
            position: 'absolute', top: 14, right: 16,
            background: 'rgba(255,255,255,.15)', border: 'none', color: '#fff',
            borderRadius: 99, width: 34, height: 34, fontSize: 17, cursor: 'pointer',
          }}>✕</button>
        </div>
      )}
    </div>
  );
}
